import type { AbTestConfig, AbTestResult } from '@/utils/abtest/abTestV3Types'

type RunningConfig<TGroup extends string> = Extract<AbTestConfig<TGroup>, { state: 'running' }>
type RatioConfig<TGroup extends string> = Extract<RunningConfig<TGroup>, { type: 'ratio' }>
type SplitPointConfig<TGroup extends string> = Extract<RunningConfig<TGroup>, { type: 'split-point' }>
type SplitPointGroups<TGroup extends string> = SplitPointConfig<TGroup>['groups']
type ResolvedRunningConfig<TGroup extends string> = {
  groups: SplitPointGroups<TGroup>
  maxSplitPoint: number
}

const AB_TEST_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1年間

// --- ABテスト判定ロジック ---

function hasInvalidWeightGroup<TGroup extends string>(config: RatioConfig<TGroup>): boolean {
  return config.groups.some(group => !Number.isInteger(group.weight) || group.weight < 0)
}

function hasInvalidSplitPointGroup<TGroup extends string>(config: SplitPointConfig<TGroup>): boolean {
  return config.groups.some(group => !Number.isInteger(group.splitPoint) || group.splitPoint <= 0)
}

function isValidRatioConfig<TGroup extends string>(config: RatioConfig<TGroup>): boolean {
  const groupNames = config.groups.map(group => group.name)
  const hasDuplicateGroupNames = hasDuplicatedValues(groupNames)
  const totalWeight = sumNumbers(config.groups.map(group => group.weight))

  return config.groups.length > 0 && !hasDuplicateGroupNames && !hasInvalidWeightGroup(config) && totalWeight > 0
}

function isValidSplitPointConfig<TGroup extends string>(config: SplitPointConfig<TGroup>): boolean {
  const groupNames = config.groups.map(group => group.name)
  const hasDuplicateGroupNames = hasDuplicatedValues(groupNames)

  const splitPoints = config.groups.map(group => group.splitPoint)
  const hasDuplicateSplitPoints = hasDuplicatedValues(splitPoints)

  return config.groups.length > 0 && !hasDuplicateGroupNames && !hasDuplicateSplitPoints && !hasInvalidSplitPointGroup(config)
}

function toSplitPointGroups<TGroup extends string>(groups: RatioConfig<TGroup>['groups']): SplitPointGroups<TGroup> {
  const result: SplitPointGroups<TGroup> = []

  for (const group of groups) {
    const lastSplitPoint = result.length > 0 ? result[result.length - 1].splitPoint : 0
    result.push({ name: group.name, splitPoint: lastSplitPoint + group.weight })
  }

  return result
}

function sortSplitPointGroups<TGroup extends string>(groups: SplitPointGroups<TGroup>): SplitPointGroups<TGroup> {
  return [...groups].sort((left, right) => left.splitPoint - right.splitPoint)
}

function pickGroup<TGroup extends string>(groups: SplitPointGroups<TGroup>, value: number): TGroup {
  for (const group of groups) {
    if (value < group.splitPoint) return group.name
  }

  return groups[groups.length - 1].name
}

function resolveRunning<TGroup extends string>(config: RunningConfig<TGroup>): AbTestResult<TGroup> {
  const resolved = parseRunningConfig(config)
  if (!resolved) {
    return { state: 'disabled' }
  }

  const value = ensureCookie(config.testName, resolved.maxSplitPoint)

  return {
    state: 'running',
    group: pickGroup(resolved.groups, value)
  }
}

function parseRunningConfig<TGroup extends string>(config: RunningConfig<TGroup>): ResolvedRunningConfig<TGroup> | undefined {
  switch (config.type) {
    case 'ratio': {
      if (!isValidRatioConfig(config)) {
        return undefined
      }

      const groups = sortSplitPointGroups(toSplitPointGroups(config.groups))
      const maxSplitPoint = groups[groups.length - 1].splitPoint
      return { groups, maxSplitPoint }
    }

    case 'split-point': {
      if (!isValidSplitPointConfig(config)) {
        return undefined
      }

      const groups = sortSplitPointGroups(config.groups)
      const maxSplitPoint = groups[groups.length - 1].splitPoint
      return { groups, maxSplitPoint }
    }
  }
}

/**
 * 指定グループに属する cookie 値を書き込み、AB テスト結果を上書きします。
 *
 * 注意:
 * - この関数は lock を利用していません。
 * - 複数箇所から同一 cookie へ同時書き込みが走ると、race condition が発生する可能性があります。
 * - AB テスト cookie の書き込み箇所は、可能な限り 1 箇所に集約してください。
 *
 * @returns 上書きした場合は `true`、それ以外は `false`。
 */
export function overwriteAbTestGroup<TGroup extends string>(config: AbTestConfig<TGroup>, group: TGroup): boolean {
  if (config.state !== 'running') return false

  const resolved = parseRunningConfig(config)
  if (!resolved) return false

  const targetRange = findGroupRange(resolved.groups, group)
  if (!targetRange) return false

  const currentValue = readCookie(config.testName)
  if (currentValue !== undefined && currentValue >= targetRange.start && currentValue < targetRange.end) {
    return false
  }

  writeCookie(config.testName, targetRange.start)
  return true
}

/**
 * AB test v3 設定から最終的な判定結果を返します。
 */
export function resolveAbTest<TGroup extends string>(config: AbTestConfig<TGroup>): AbTestResult<TGroup> {
  switch (config.state) {
    case 'disabled':
      return { state: 'disabled' }

    case 'stopped':
      return {
        state: 'stopped',
        group: config.selectedGroup
      }

    case 'running':
      return resolveRunning(config)
  }
}

// --- 補助ヘルパー関数 ---

function getAbTestCookieKey(testName: string): string {
  return `${testName.toLowerCase()}_ab_uid`
}

function readCookie(testName: string): number | undefined {
  const cookieValue = useCookie(getAbTestCookieKey(testName), {
    maxAge: AB_TEST_COOKIE_MAX_AGE
  })

  const rawValue = cookieValue.value
  if (rawValue === undefined || rawValue === null || rawValue === '') return undefined

  const parsedValue = Number(rawValue)
  if (!Number.isInteger(parsedValue) || parsedValue < 0) return undefined

  return parsedValue
}

function writeCookie(testName: string, value: number): void {
  const cookieName = getAbTestCookieKey(testName)
  const cookieValue = useCookie(cookieName, {
    maxAge: AB_TEST_COOKIE_MAX_AGE
  })

  cookieValue.value = String(value)

  if (!isProduction()) {
    console.log(`[AB Test v3] Cookie written: ${cookieName} = ${value}`)
  }
}

function ensureCookie(testName: string, max: number): number {
  const currentValue = readCookie(testName)
  if (currentValue !== undefined && currentValue < max) return currentValue

  const nextValue = Math.floor(Math.random() * max)
  writeCookie(testName, nextValue)

  return nextValue
}

function hasDuplicatedValues<TValue>(values: TValue[]): boolean {
  return new Set(values).size !== values.length
}

function sumNumbers(numbers: number[]): number {
  return numbers.reduce((sum, number) => sum + number, 0)
}

function findGroupRange<TGroup extends string>(groups: SplitPointGroups<TGroup>, targetGroup: TGroup) {
  let start = 0

  for (const group of groups) {
    if (group.name === targetGroup) {
      return {
        start,
        end: group.splitPoint
      }
    }
    start = group.splitPoint
  }

  return undefined
}
